/**
 * Tenant detail — `GET tenants/tenants/{id}/`. Same role-dependent shape split
 * as the list page; see `isEnhancedTenant` in src/types/tenant.ts.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { getTenant, listLeases } from '../../api/endpoints/tenants';
import { isEnhancedTenant } from '../../types/tenant';
import { fullName } from '../../types/user';

export function TenantDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const tenantId = Number(id);

  const query = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenant(tenantId),
    enabled: Number.isFinite(tenantId),
  });

  // Lease dates are on the lease, not on either tenant serializer, so they need
  // their own fetch. The list comes back newest-first (the backend falls back to
  // `Lease.Meta.ordering = ['-created_at']`), so the first row is the current
  // lease when a tenant has had more than one.
  const leases = useQuery({
    queryKey: ['tenant-leases', tenantId],
    queryFn: () => listLeases({ tenant: tenantId }),
    enabled: Number.isFinite(tenantId),
  });
  const lease = leases.data?.results[0] ?? null;

  if (!Number.isFinite(tenantId)) return <p>Invalid tenant id.</p>;
  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const tenant = query.data;
  const enhanced = isEnhancedTenant(tenant);

  return (
    <section>
      <h2>{enhanced ? tenant.full_name : fullName(tenant)}</h2>
      <p>
        <Link to="edit">Edit</Link>
      </p>
      <dl>
        <dt>Email</dt>
        <dd>{tenant.email}</dd>
        <dt>Phone</dt>
        <dd>{tenant.phone === '' ? '—' : tenant.phone}</dd>
        <dt>Property</dt>
        <dd>{enhanced ? tenant.property_name : (tenant.property_name ?? 'N/A')}</dd>
        <dt>Unit</dt>
        <dd>{enhanced ? tenant.unit_number : (tenant.unit_number ?? 'N/A')}</dd>
        <dt>Lease status</dt>
        <dd>{tenant.lease_status}</dd>
        <dt>Lease start</dt>
        <dd>{lease?.start_date ?? '—'}</dd>
        <dt>Lease end</dt>
        <dd>{lease?.end_date ?? '—'}</dd>
        <dt>Current balance</dt>
        <dd>{tenant.current_balance}</dd>
        {enhanced && (
          <>
            <dt>Last payment</dt>
            <dd>
              {tenant.last_payment_date === null
                ? 'No payments yet'
                : `${tenant.last_payment_amount} on ${tenant.last_payment_date}`}
            </dd>
          </>
        )}
        {/*
          Role-conditional: emergency contact lives on the nested `profile` of the
          plain `User` shape, which only LANDLORD callers receive. The enhanced
          serializer AGENT/SUPERAGENT callers get has no `profile` field at all,
          so for them there is nothing to show rather than something empty.
        */}
        {!enhanced && tenant.profile !== null && (
          <>
            <dt>Emergency contact</dt>
            <dd>
              {tenant.profile.emergency_contact_name === ''
                ? '—'
                : tenant.profile.emergency_contact_name}
            </dd>
            <dt>Emergency contact phone</dt>
            <dd>
              {tenant.profile.emergency_contact_phone === ''
                ? '—'
                : tenant.profile.emergency_contact_phone}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

export default TenantDetailPage;
